import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { APP_NAME } from '../config/index.js';
import { getPageById, getPageBySlug, routeForPageId, routeForSlug } from './menuData.js';
import { useTenantBranding } from '../context/TenantBrandingContext.jsx';
import HelpDrawer from '../help/HelpDrawer.jsx';
import { accessRole, canAccessSlug } from '../config/routeAccess.js';
import { landingRouteForRole } from '../pages/sign-in/roleLanding.js';
import { formatMoney } from '../utils/money.js';
import './AppShell.scss';

const LOGIN_ROUTE = () => routeForSlug('sign-in');
const canOpenPage = (role, pageId) => canAccessSlug(role, getPageById(pageId)?.slug);

// Brand link target: the layout's default page when the role may open it, otherwise the role's own landing page.
function homeRouteFor(menu, role) {
  if (menu?.defaultPageId && canOpenPage(role, menu.defaultPageId)) return routeForPageId(menu.defaultPageId);
  const landing = landingRouteForRole(role);
  return canAccessSlug(role, landing.slice(1)) ? landing : '/';
}

// Residents and technicians are not back-office staff: show their role and their two or three
// destinations in their own words, and leave out pages that are only reachable from a scanned code.
const ROLE_DISPLAY = { raiser: 'Resident', worker: 'Technician' };
const FIELD_NAV = {
  raiser: {
    hidden: ['qr-defect-intake-pwa'],
    labels: { 'resident-portal-defect-submission': 'My requests', 'dual-sign-off-resident-verification': 'Confirm repair' },
  },
  worker: {
    hidden: ['qr-defect-intake-pwa'],
    labels: { 'technician-mobile-execution-pwa': 'My jobs', 'dual-sign-off-resident-verification': 'Sign-off' },
  },
};
const roleKey = (role) => String(role ?? '').toLowerCase();
const displayRole = (role) => ROLE_DISPLAY[roleKey(role)] || role;

// A suspended customer (user.tenant.status, from GET /me): what the API still lets each role do decides the wording.
// Residents and the service desk are unaffected, so they see no banner.
const BILLING_SLUG = 'invoicing-stripe-billing-portal';
const SUSPENSION_UNAFFECTED = ['raiser', 'service desk agent'];
const SUSPENSION_VIEWS = { 'customer admin': 'billing', finance: 'billing', manager: 'read-only', auditor: 'read-only' };
const SUSPENSION_TEXT = {
  billing: 'Your account is suspended for non-payment. Billing is still available so you can settle the balance.',
  'read-only': 'Your account is suspended. You can view records, but changes are paused.',
  paused: 'Service is paused for this account. Please contact your administrator.',
};
function suspensionViewFor(user) {
  if (user?.tenant?.status !== 'SUSPENDED') return null;
  const key = roleKey(user.role);
  if (SUSPENSION_UNAFFECTED.includes(key)) return null;
  return SUSPENSION_VIEWS[key] || 'paused';
}

// A late payment (user.billingNotice, from GET /me). The server decides who is addressed and how urgently; this
// only words it. Billing contacts (canPay) see the amount and a way to pay, everyone else is asked to pass it on.
// A suspended customer sees the suspension banner instead, which already explains the situation.
const BILLING_NOTICE_LEVELS = { REMINDER: 'reminder', WARNING: 'warning', FINAL: 'final' };
const BILLING_NOTICE_ICONS = { reminder: 'fa-info-circle', warning: 'fa-exclamation-triangle', final: 'fa-exclamation-circle' };
function billingNoticeFor(user) {
  const notice = user?.billingNotice;
  if (!notice || user?.tenant?.status === 'SUSPENDED') return null;
  const tone = BILLING_NOTICE_LEVELS[notice.level] || 'reminder';
  const late = `${notice.daysLate} ${notice.daysLate === 1 ? 'day' : 'days'} overdue`;
  const canPay = Boolean(notice.canPay);
  let text;
  if (canPay) {
    const due = notice.amountDueCents == null ? '' : ` — ${formatMoney(notice.amountDueCents, notice.currency)} due`;
    text = tone === 'final' ? `Final notice: payment is ${late}${due}. Service may be suspended.` : `Payment is ${late}${due}.`;
  } else if (tone === 'final') {
    text = `Final notice: payment for this account is ${late}. Service may be suspended.`;
  } else {
    text = `Payment for this account is ${late}. Please ask your administrator to settle it.`;
  }
  return { tone, level: notice.level, canPay, text, showLink: canPay && canAccessSlug(accessRole(user), BILLING_SLUG) };
}

// Pages that only make sense inside a property company. A contractor's technician signs in with the same role as
// an in-house one but belongs to no company, so these are left out of their menu.
const COMPANY_ONLY_SLUGS = ['inspections'];

// Open pages (anyone may load them) that still belong in only some roles' menus: the QR intake form is what a
// scanned placard opens, and only the roles that manage intake need a link to it.
const MENU_ONLY_FOR = { 'qr-defect-intake-pwa': ['supervisor', 'manager', 'customer admin'] };
const inMenuFor = (role, slug) => !MENU_ONLY_FOR[slug] || MENU_ONLY_FOR[slug].includes(roleKey(role));

/** Menu tree limited to the pages `role` may open (minus the `without` slugs); groups left without a page are dropped. */
function filterMenuForRole(items, role, without = []) {
  const field = FIELD_NAV[roleKey(role)];
  return items.flatMap((item) => {
    if (item.children) {
      const children = filterMenuForRole(item.children, role, without);
      return children.length ? [{ ...item, children }] : [];
    }
    if (!item.pageId) return [item];
    const slug = getPageById(item.pageId)?.slug;
    if (!canAccessSlug(role, slug) || field?.hidden.includes(slug) || without.includes(slug) || !inMenuFor(role, slug)) return [];
    return [field?.labels[slug] ? { ...item, label: field.labels[slug] } : item];
  });
}

function flattenLeaves(items, out = []) {
  items.forEach((item) => {
    if (item.pageId) out.push(item);
    if (item.children) flattenLeaves(item.children, out);
  });
  return out;
}

function isActive(pathname, pageId) {
  const page = getPageById(pageId);
  return !!page && (pathname === '/' + page.slug || pathname.startsWith('/' + page.slug + '/'));
}

function DesktopMenuItem({ item, pathname }) {
  if (item.children) {
    const groupActive = flattenLeaves(item.children).some((c) => isActive(pathname, c.pageId));
    return (
      <li className="nav-item dropdown">
        <a
          className={'nav-link dropdown-toggle' + (groupActive ? ' active' : '')}
          href="#"
          role="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          onClick={(e) => e.preventDefault()}
        >
          <i className={item.icon + ' me-1'} aria-hidden="true"></i> {item.label}
        </a>
        <ul className="dropdown-menu shadow border-0">
          {item.children.map((child) => (
            <li key={child.label}>
              {child.children ? (
                <span className="dropdown-header">{child.label}</span>
              ) : (
                <Link
                  className={'dropdown-item py-2' + (isActive(pathname, child.pageId) ? ' active' : '')}
                  to={routeForPageId(child.pageId)}
                >
                  <i className={child.icon + ' me-2'} aria-hidden="true"></i>
                  {child.label}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </li>
    );
  }
  return (
    <li className="nav-item">
      <Link
        className={'nav-link' + (isActive(pathname, item.pageId) ? ' active' : '')}
        to={routeForPageId(item.pageId)}
      >
        <i className={item.icon + ' me-1'} aria-hidden="true"></i> {item.label}
      </Link>
    </li>
  );
}

export default function AppShell({ layoutName, menu }) {
  const { user, isAuthenticated, logout } = useAuth();
  const { branding, logoSrc } = useTenantBranding();
  const brandName = branding.orgName || APP_NAME;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  const pageRole = accessRole(user);
  const inCompany = Boolean(user?.tenant);
  const items = useMemo(() => filterMenuForRole(menu?.items || [], pageRole, inCompany ? [] : COMPANY_ONLY_SLUGS), [menu, pageRole, inCompany]);
  const leaves = useMemo(() => flattenLeaves(items), [items]);
  const bottomTabs = useMemo(() => leaves.filter((l) => l.bottomBar).slice(0, 4), [leaves]);
  const home = homeRouteFor(menu, pageRole);
  const suspensionView = suspensionViewFor(user);
  const billingNotice = billingNoticeFor(user);
  const billingNoticeText = billingNotice?.text;
  const shellRef = useRef(null);
  const bannerRef = useRef(null);
  const billingNoticeRef = useRef(null);

  useEffect(() => {
    document.body.classList.add('has-app-shell');
    return () => document.body.classList.remove('has-app-shell');
  }, []);

  useEffect(() => {
    setMoreOpen(false);
    setAccountOpen(false);
  }, [pathname]);

  // The suspension and late-payment banners are fixed under the top bar, the late-payment one below the other.
  // Their heights vary with the wording and the screen width, so measure them (and the bar they hang from) and let
  // the stylesheet stack them and push the page content down by their combined height.
  useEffect(() => {
    if (!suspensionView && !billingNoticeText) return undefined;
    const body = document.body;
    const heightOf = (ref) => `${ref.current?.getBoundingClientRect().height || 0}px`;
    const measure = () => {
      const bars = shellRef.current?.querySelectorAll('.app-top-navbar, .mobile-only-header') || [];
      const bar = Array.from(bars).find((el) => el.getBoundingClientRect().height > 0);
      if (bar) body.style.setProperty('--app-top-bar-h', `${bar.getBoundingClientRect().height}px`);
      body.style.setProperty('--suspension-banner-h', heightOf(bannerRef));
      body.style.setProperty('--billing-notice-h', heightOf(billingNoticeRef));
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (bannerRef.current) observer.observe(bannerRef.current);
    if (billingNoticeRef.current) observer.observe(billingNoticeRef.current);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      body.style.removeProperty('--app-top-bar-h');
      body.style.removeProperty('--suspension-banner-h');
      body.style.removeProperty('--billing-notice-h');
    };
  }, [suspensionView, billingNoticeText]);

  // Per-page browser tab title (history, bookmarks and screen readers all rely on it).
  useEffect(() => {
    const page = getPageBySlug(pathname.slice(1));
    document.title = page ? `${page.name} · ${brandName}` : brandName;
  }, [pathname, brandName]);

  const handleLogout = async () => {
    await logout();
    navigate(LOGIN_ROUTE());
  };

  const handleLogoutEverywhere = async () => {
    await logout({ everywhere: true });
    navigate(LOGIN_ROUTE());
  };

  const displayName = user?.name || user?.email || 'Account';
  const role = displayRole(pageRole);

  return (
    <div className="app-shell" data-layout={layoutName} ref={shellRef}>
      {/* 1. DESKTOP HEADER (>= 992px) */}
      <header className="desktop-only-header">
        <nav className="navbar navbar-expand-lg navbar-dark app-top-navbar fixed-top" aria-label="Primary">
          <div className="container-fluid px-3">
            <Link className="navbar-brand d-flex align-items-center gap-2" to={home}>
              {logoSrc ? (
                <img src={logoSrc} alt="" className="app-brand-logo" data-testid="tenant-logo" style={{ height: 28, maxWidth: 140, objectFit: 'contain' }} />
              ) : (
                <i className="fas fa-clipboard-check app-brand-icon" aria-hidden="true"></i>
              )}
              <span className="fw-bold app-brand-text">{brandName}</span>
            </Link>

            <div className="navbar-collapse show" id="navbarNav">
              <ul className="navbar-nav me-auto mb-2 mb-lg-0" id="navbar-nav">
                {items.map((item) => (
                  <DesktopMenuItem key={item.label} item={item} pathname={pathname} />
                ))}
              </ul>
            </div>

            <div className="d-flex align-items-center gap-3 ms-auto">
              <HelpDrawer />
              {isAuthenticated ? (
                <div className="dropdown">
                  <button
                    className="btn btn-sm btn-outline-light dropdown-toggle d-flex align-items-center gap-2"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    aria-label="Account menu"
                  >
                    {role && (
                      <span className="desktop-role-badge">
                        <i className="fas fa-user-shield" aria-hidden="true"></i> {role}
                      </span>
                    )}
                    <span className="small fw-medium">{displayName}</span>
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end shadow border-0">
                    <li className="dropdown-header">
                      <span className="d-block fw-semibold text-body">{displayName}</span>
                      {user?.email}
                    </li>
                    <li>
                      <hr className="dropdown-divider" />
                    </li>
                    <li>
                      <button type="button" className="dropdown-item text-danger py-2" onClick={handleLogout}>
                        <i className="fas fa-sign-out-alt me-2" aria-hidden="true"></i> Sign Out
                      </button>
                    </li>
                    <li>
                      <button type="button" className="dropdown-item py-2" onClick={handleLogoutEverywhere}>
                        <i className="fas fa-globe me-2" aria-hidden="true"></i> Sign Out Everywhere
                      </button>
                    </li>
                  </ul>
                </div>
              ) : (
                <Link className="btn btn-sm btn-outline-light" to={LOGIN_ROUTE()}>
                  <i className="fas fa-sign-in-alt me-1" aria-hidden="true"></i> Sign In
                </Link>
              )}
            </div>
          </div>
        </nav>
      </header>

      {/* 2. MOBILE TOP APP BAR (< 992px) */}
      <header className="mobile-only-header">
        <Link to={home} className="mobile-brand-title">
          {logoSrc ? <img src={logoSrc} alt="" style={{ height: 24, maxWidth: 110, objectFit: 'contain' }} /> : <i className="fas fa-clipboard-check" aria-hidden="true"></i>}
          <span>{brandName}</span>
        </Link>
        <div className="d-flex align-items-center gap-2">
          <HelpDrawer className="btn btn-sm btn-outline-light py-0 px-2 border-0" />
          {isAuthenticated ? (
            <>
              <button
                type="button"
                className="mobile-role-pill-btn"
                onClick={() => setAccountOpen(true)}
                aria-label="Account details"
              >
                <i className="fas fa-user-circle" aria-hidden="true"></i>
                <span>{role || displayName}</span>
                <i className="fas fa-chevron-down" style={{ fontSize: 8, opacity: 0.8 }} aria-hidden="true"></i>
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-light py-0 px-2 border-0"
                title="Sign Out"
                aria-label="Sign Out"
                onClick={handleLogout}
              >
                <i className="fas fa-sign-out-alt" aria-hidden="true"></i>
              </button>
            </>
          ) : (
            <Link to={LOGIN_ROUTE()} className="btn btn-sm btn-outline-light py-0 px-2 border-0" title="Sign In">
              <i className="fas fa-sign-in-alt" aria-hidden="true"></i> <span className="small">Sign In</span>
            </Link>
          )}
        </div>
      </header>

      {/* SUSPENSION BANNER (desktop and mobile): persistent, directly under the top bar */}
      {suspensionView && (
        <div className={`suspension-banner suspension-banner-${suspensionView}`} role="status" ref={bannerRef} data-testid="suspension-banner" data-role-view={suspensionView}>
          <i className="fas fa-pause-circle" aria-hidden="true"></i>
          <span data-testid="suspension-banner-text">{SUSPENSION_TEXT[suspensionView]}</span>
          {suspensionView === 'billing' && canAccessSlug(pageRole, BILLING_SLUG) && (
            <Link to={routeForSlug(BILLING_SLUG)} className="suspension-banner-link" data-testid="suspension-banner-billing-link">
              Go to invoices
            </Link>
          )}
        </div>
      )}

      {/* LATE-PAYMENT BANNER (desktop and mobile): persistent, under the top bar and any suspension banner */}
      {billingNotice && (
        <div
          className={`billing-notice-banner billing-notice-${billingNotice.tone}`}
          role="status"
          ref={billingNoticeRef}
          data-testid="billing-notice-banner"
          data-level={billingNotice.level}
          data-can-pay={billingNotice.canPay}
        >
          <i className={`fas ${BILLING_NOTICE_ICONS[billingNotice.tone]}`} aria-hidden="true"></i>
          <span data-testid="billing-notice-text">{billingNotice.text}</span>
          {billingNotice.showLink && (
            <Link to={routeForSlug(BILLING_SLUG)} className="billing-notice-link" data-testid="billing-notice-link">
              View invoices
            </Link>
          )}
        </div>
      )}

      {/* 3. MAIN CONTENT WRAPPER */}
      <main className="main-content-wrapper">
        <Outlet />
      </main>

      {/* 4. MOBILE BOTTOM NAVIGATION (< 992px) */}
      <nav id="mobile-bottom-nav" aria-label="Mobile Navigation">
        {bottomTabs.map((tab) => (
          <Link
            key={tab.pageId}
            to={routeForPageId(tab.pageId)}
            className={'mobile-nav-tab' + (isActive(pathname, tab.pageId) ? ' active' : '')}
          >
            <i className={tab.icon} aria-hidden="true"></i>
            <span>{tab.label}</span>
          </Link>
        ))}
        {leaves.length > bottomTabs.length && (
          <button type="button" className="mobile-nav-tab" onClick={() => setMoreOpen(true)}>
            <i className="fas fa-th-large" aria-hidden="true"></i>
            <span>More</span>
          </button>
        )}
      </nav>

      {/* 5. SLIDE-UP ACCOUNT SHEET */}
      {accountOpen && (
        <div
          className="mobile-drawer-sheet is-open"
          role="presentation"
          onClick={() => setAccountOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setAccountOpen(false)}
        >
          <div className="mobile-drawer-content" role="dialog" aria-label="Account" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.key !== 'Escape' && e.stopPropagation()}>
            <div className="sheet-handle-bar"></div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="fw-bold mb-0">
                <i className="fas fa-user-circle text-primary me-2" aria-hidden="true"></i>
                {displayName}
              </h6>
              <button type="button" className="btn-close small" onClick={() => setAccountOpen(false)} aria-label="Close"></button>
            </div>
            <p className="small text-muted mb-2">
              {user?.email}
              {role ? ' · ' + role : ''}
            </p>
            <button type="button" className="btn btn-outline-danger w-100" onClick={handleLogout}>
              <i className="fas fa-sign-out-alt me-2" aria-hidden="true"></i> Sign Out
            </button>
            <button type="button" className="btn btn-link text-secondary w-100 mt-2" onClick={handleLogoutEverywhere}>
              Sign Out Everywhere
            </button>
          </div>
        </div>
      )}

      {/* 6. SLIDE-UP "MORE" SHEET */}
      {moreOpen && (
        <div
          className="mobile-drawer-sheet is-open"
          role="presentation"
          onClick={() => setMoreOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setMoreOpen(false)}
        >
          <div className="mobile-drawer-content" role="dialog" aria-label={'All ' + APP_NAME + ' pages'} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.key !== 'Escape' && e.stopPropagation()}>
            <div className="sheet-handle-bar"></div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="fw-bold mb-0">
                <i className="fas fa-th-large text-primary me-2" aria-hidden="true"></i>All pages
              </h6>
              <button type="button" className="btn-close small" onClick={() => setMoreOpen(false)} aria-label="Close"></button>
            </div>
            <div className="portals-grid">
              {leaves.map((leaf) => (
                <Link key={leaf.pageId} to={routeForPageId(leaf.pageId)} className="portal-grid-item">
                  <i className={leaf.icon} aria-hidden="true"></i>
                  <span>{leaf.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
